import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import HeritageLogo from "./HeritageLogo";
import { Magnetic } from "./Motion";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/admissions", label: "Admissions" },
  { to: "/faculty", label: "Faculty" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -28, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`glass-nav sticky top-0 z-50 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]" : ""
      }`}
      data-testid="site-navbar"
    >
      <div className="hccbt-container flex items-center justify-between h-20">
        <Link to="/" className="flex items-center group" data-testid="navbar-logo-link">
          <span className="transition-transform duration-300 group-hover:scale-[1.03]">
            <HeritageLogo className="h-10 w-auto" />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `link-underline text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#0F172A] is-active" : "text-[#475569] hover:text-[#0F172A]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <Magnetic strength={10}>
            <Link
              to="/admissions"
              className="btn-primary text-sm"
              data-testid="navbar-apply-now-button"
            >
              Apply Now
            </Link>
          </Magnetic>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          className="lg:hidden p-2 rounded-full border border-[#E2E8F0] text-[#0F172A]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden border-t border-[#E2E8F0] bg-[#FDFBF7] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="hccbt-container py-4 flex flex-col gap-1">
              {navItems.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block px-3 py-3 rounded-lg text-sm ${
                        isActive ? "bg-[#87CEEB]/20 text-[#0F172A]" : "text-[#334155] hover:bg-[#F1F5F9]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/admissions"
                onClick={() => setOpen(false)}
                className="btn-primary mt-3 justify-center"
                data-testid="mobile-apply-now-button"
              >
                Apply Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
